/**
 * StatusBadge Component
 *
 * Clickable status badge with dropdown. Supports per-diligence disallowed statuses
 * and required anomaly note (B1-02 summary omission).
 */

import React, { useState, useRef, useEffect } from 'react';

export type DiligenceStatus = 'todo' | 'revise' | 'supervise' | 'non_applicable' | 'anomalie';

export type StatusChangeMeta = {
  anomalyNote?: string;
};

export type StatusBadgeProps = {
  currentStatus: DiligenceStatus;
  onStatusChange: (newStatus: DiligenceStatus, meta?: StatusChangeMeta) => void | Promise<void>;
  className?: string;
  dropdownClassName?: string;
  disabled?: boolean;
  /** When false, révisée is hidden from the dropdown (perm.reports.review). Defaults to true. */
  canReview?: boolean;
  /** When false, supervisée and anomalie are hidden (perm.reports.supervise). Defaults to false. */
  canSupervise?: boolean;
  disallowedStatuses?: DiligenceStatus[];
  requireAnomalyNote?: boolean;
  /** Unsaved form omission will sync this status on next save. */
  isStatusSyncPending?: boolean;
  statusSyncPendingTitle?: string;
};

export const STATUS_LABELS: Record<DiligenceStatus, string> = {
  todo: 'À faire',
  revise: 'Révisée',
  supervise: 'Supervisée',
  non_applicable: 'Non applicable',
  anomalie: 'Anomalie',
};

const getAllowedStatuses = (
  currentStatus: DiligenceStatus,
  disallowedStatuses: DiligenceStatus[],
  canReview: boolean,
  canSupervise: boolean,
): DiligenceStatus[] => {
  const allStatuses: DiligenceStatus[] = ['todo', 'supervise', 'revise', 'non_applicable', 'anomalie'];

  let allowedStatuses = allStatuses;

  // Supervising implies reviewing, so révisée stays on offer while either level is held. The
  // implication runs one way: reviewing does not grant supervisée, nor flagging an anomalie,
  // which is a supervision-level judgement. Mirrors DiligenceSuperviseAuthorization and
  // VsmePermissionChecker::check_review_permission — a disagreement here is either a dead
  // button (403 on click) or a capability the user never discovers.
  if (!canReview && !canSupervise) {
    allowedStatuses = allowedStatuses.filter((status) => status !== 'revise');
  }

  if (!canSupervise) {
    allowedStatuses = allowedStatuses.filter(
      (status) => status !== 'supervise' && status !== 'anomalie',
    );
  }

  if (disallowedStatuses.length > 0) {
    allowedStatuses = allowedStatuses.filter((status) => !disallowedStatuses.includes(status));
  }

  return allowedStatuses.filter((status) => status !== currentStatus);
};

const StatusBadge = ({
  currentStatus,
  onStatusChange,
  className = '',
  dropdownClassName = '',
  disabled = false,
  canReview = true,
  canSupervise = false,
  disallowedStatuses = [],
  requireAnomalyNote = false,
  isStatusSyncPending = false,
  statusSyncPendingTitle = 'Non enregistré',
}: StatusBadgeProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingAnomalyStatus, setPendingAnomalyStatus] = useState(false);
  const [anomalyNote, setAnomalyNote] = useState('');
  const [anomalyNoteError, setAnomalyNoteError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);

  const availableStatuses = getAllowedStatuses(currentStatus, disallowedStatuses, canReview, canSupervise);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setPendingAnomalyStatus(false);
        setAnomalyNote('');
        setAnomalyNoteError('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setPendingAnomalyStatus(false);
        setAnomalyNote('');
        setAnomalyNoteError('');
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (pendingAnomalyStatus) {
      noteInputRef.current?.focus();
    }
  }, [pendingAnomalyStatus]);

  const handleBadgeClick = () => {
    if (disabled || isUpdating) return;
    setIsOpen(!isOpen);
    if (isOpen) {
      setPendingAnomalyStatus(false);
      setAnomalyNote('');
      setAnomalyNoteError('');
    }
  };

  const submitStatusChange = async (newStatus: DiligenceStatus, meta?: StatusChangeMeta) => {
    setIsUpdating(true);
    try {
      await onStatusChange(newStatus, meta);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusSelect = async (newStatus: DiligenceStatus) => {
    if (disabled || isUpdating) return;

    if (newStatus === 'anomalie' && requireAnomalyNote) {
      setPendingAnomalyStatus(true);
      return;
    }

    setIsOpen(false);
    await submitStatusChange(newStatus);
  };

  const handleAnomalyNoteSubmit = async () => {
    const trimmed = anomalyNote.trim();
    if (trimmed === '') {
      setAnomalyNoteError('Une note est requise.');
      return;
    }

    setAnomalyNoteError('');
    setIsOpen(false);
    setPendingAnomalyStatus(false);
    const note = trimmed;
    setAnomalyNote('');
    await submitStatusChange('anomalie', { anomalyNote: note });
  };

  const baseClass = className || 'dilig-cycle__item-status';

  const getStatusBadgeClass = (status: DiligenceStatus): string =>
    `${baseClass} ${baseClass}--${status}`;

  return (
    <div className="status-badge-container">
      <button
        ref={buttonRef}
        type="button"
        className={`${getStatusBadgeClass(currentStatus)} ${isUpdating ? `${baseClass}--updating` : ''} ${isStatusSyncPending ? `${baseClass}--pending-sync` : ''}`}
        onClick={handleBadgeClick}
        disabled={disabled || isUpdating}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={isStatusSyncPending ? statusSyncPendingTitle : undefined}
        aria-description={isStatusSyncPending ? statusSyncPendingTitle : undefined}
      >
        {STATUS_LABELS[currentStatus]}
        {isUpdating ? ' ...' : ''}
      </button>

      {isOpen && (availableStatuses.length > 0 || pendingAnomalyStatus) && (
        <div
          ref={dropdownRef}
          className={`status-badge-dropdown ${dropdownClassName}`.trim()}
          role="listbox"
          aria-label="Changer le statut"
        >
          {pendingAnomalyStatus ? (
            <div className="status-badge-dropdown__anomaly-form">
              <label className="status-badge-dropdown__anomaly-label" htmlFor="status-badge-anomaly-note">
                Note d&apos;anomalie (obligatoire)
              </label>
              <textarea
                id="status-badge-anomaly-note"
                ref={noteInputRef}
                className="status-badge-dropdown__anomaly-input"
                rows={4}
                value={anomalyNote}
                onChange={(event) => {
                  setAnomalyNote(event.target.value);
                  if (anomalyNoteError !== '') {
                    setAnomalyNoteError('');
                  }
                }}
              />
              {anomalyNoteError !== '' ? (
                <p className="status-badge-dropdown__anomaly-error">{anomalyNoteError}</p>
              ) : null}
              <div className="status-badge-dropdown__anomaly-actions">
                <button
                  type="button"
                  className="status-badge-dropdown__option"
                  onClick={() => {
                    setPendingAnomalyStatus(false);
                    setAnomalyNote('');
                    setAnomalyNoteError('');
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className={`status-badge-dropdown__option ${getStatusBadgeClass('anomalie')}`}
                  onClick={() => void handleAnomalyNoteSubmit()}
                  disabled={isUpdating}
                >
                  Confirmer l&apos;anomalie
                </button>
              </div>
            </div>
          ) : (
            availableStatuses.map((status) => (
              <button
                key={status}
                type="button"
                className={`status-badge-dropdown__option ${getStatusBadgeClass(status)}`}
                onClick={() => void handleStatusSelect(status)}
                role="option"
                aria-selected={false}
              >
                {STATUS_LABELS[status]}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default StatusBadge;
